import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { 
  Plus, 
  Search, 
  Filter,
  MoreHorizontal,
  Edit,
  Trash2,
  CheckCircle,
  XCircle,
  Store,
  Globe,
  Mail,
  Phone,
  MapPin
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const Partners = () => {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [showDialog, setShowDialog] = useState(false);
  const [editingPartner, setEditingPartner] = useState(null);
  const [formData, setFormData] = useState({
    business_name: "",
    contact_name: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    state: "",
    zip_code: "",
    business_type: "",
    website: "",
    notes: ""
  });

  useEffect(() => {
    fetchPartners();
  }, [statusFilter]);

  const fetchPartners = async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.append("status", statusFilter);
      if (search) params.append("search", search);
      
      const response = await axios.get(`${API}/partners?${params}`);
      setPartners(response.data.items || []);
    } catch (error) {
      toast.error("Failed to fetch partners");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchPartners();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingPartner) {
        await axios.put(`${API}/partners/${editingPartner.id}`, formData);
        toast.success("Partner updated successfully");
      } else {
        await axios.post(`${API}/partners`, formData);
        toast.success("Partner registered successfully");
      }
      setShowDialog(false);
      resetForm();
      fetchPartners();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save partner");
    }
  };

  const handleEdit = (partner) => {
    setEditingPartner(partner);
    setFormData({
      business_name: partner.business_name,
      contact_name: partner.contact_name,
      email: partner.email,
      phone: partner.phone,
      address: partner.address,
      city: partner.city,
      state: partner.state,
      zip_code: partner.zip_code,
      business_type: partner.business_type,
      website: partner.website || "",
      notes: partner.notes || ""
    });
    setShowDialog(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this partner?")) return;
    try {
      await axios.delete(`${API}/partners/${id}`);
      toast.success("Partner deleted");
      fetchPartners();
    } catch (error) {
      toast.error("Failed to delete partner");
    }
  };

  const handleStatusUpdate = async (id, newStatus) => {
    try {
      await axios.put(`${API}/partners/${id}`, { status: newStatus });
      toast.success(`Partner ${newStatus}`);
      fetchPartners();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const resetForm = () => {
    setEditingPartner(null);
    setFormData({
      business_name: "",
      contact_name: "",
      email: "",
      phone: "",
      address: "",
      city: "",
      state: "",
      zip_code: "",
      business_type: "",
      website: "",
      notes: ""
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const getStatusBadge = (status) => {
    const styles = {
      approved: "bg-green-100 text-green-800 border-green-200",
      pending: "bg-yellow-100 text-yellow-800 border-yellow-200",
      suspended: "bg-orange-100 text-orange-800 border-orange-200"
    };
    return (
      <Badge variant="outline" className={styles[status] || styles.pending}>
        {status}
      </Badge>
    );
  };

  const getBusinessTypeBadge = (type) => {
    const labels = {
      clothing_retailer: "Clothing Retailer",
      thrift_store: "Thrift Store",
      nonprofit: "Nonprofit",
      discount_retailer: "Discount Retailer",
      department_store: "Department Store"
    };
    return labels[type] || type;
  };

  return (
    <div className="space-y-6" data-testid="partners-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Playfair Display, serif' }}>
            Partner Retailers
          </h1>
          <p className="text-slate-500 mt-1">Manage retail partners accepting vouchers</p>
        </div>
        <Button 
          className="bg-amber-600 hover:bg-amber-700 text-white gap-2"
          onClick={() => { resetForm(); setShowDialog(true); }}
          data-testid="add-partner-btn"
        >
          <Plus className="w-4 h-4" />
          Add Partner
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <form onSubmit={handleSearch} className="flex-1 flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search by business name..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9"
                  data-testid="search-input"
                />
              </div>
              <Button type="submit" variant="secondary">Search</Button>
            </form>
            <Select value={statusFilter || "all"} onValueChange={(v) => setStatusFilter(v === "all" ? "" : v)}>
              <SelectTrigger className="w-[180px]" data-testid="status-filter">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="suspended">Suspended</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Business</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead>Location</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Vouchers Redeemed</TableHead>
                <TableHead>Revenue</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-[80px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8">
                    <div className="animate-pulse">Loading...</div>
                  </TableCell>
                </TableRow>
              ) : partners.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-slate-500">
                    No partners found
                  </TableCell>
                </TableRow>
              ) : (
                partners.map((partner) => (
                  <TableRow key={partner.id} data-testid={`partner-row-${partner.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 bg-green-100 rounded-lg flex items-center justify-center">
                          <Store className="w-4 h-4 text-green-600" />
                        </div>
                        <div>
                          <div className="font-medium">{partner.business_name}</div>
                          {partner.website && (
                            <a 
                              href={partner.website.startsWith('http') ? partner.website : `https://${partner.website}`} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-xs text-amber-600 hover:underline flex items-center gap-1"
                            >
                              <Globe className="w-3 h-3" /> Website
                            </a>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">
                        <div className="font-medium">{partner.contact_name}</div>
                        <div className="flex items-center gap-1 text-slate-500">
                          <Mail className="w-3 h-3" /> {partner.email}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 text-slate-600 text-sm">
                        <MapPin className="w-3 h-3" />
                        {partner.city}, {partner.state}
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-slate-600">
                        {getBusinessTypeBadge(partner.business_type)}
                      </span>
                    </TableCell>
                    <TableCell className="font-medium text-center">
                      {partner.vouchers_redeemed || 0}
                    </TableCell>
                    <TableCell className="font-medium text-green-600">
                      {formatCurrency(partner.total_revenue)}
                    </TableCell>
                    <TableCell>{getStatusBadge(partner.status)}</TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" data-testid={`actions-${partner.id}`}>
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleEdit(partner)}>
                            <Edit className="w-4 h-4 mr-2" /> Edit
                          </DropdownMenuItem>
                          {partner.status === "pending" && (
                            <DropdownMenuItem onClick={() => handleStatusUpdate(partner.id, "approved")}>
                              <CheckCircle className="w-4 h-4 mr-2 text-green-600" /> Approve
                            </DropdownMenuItem>
                          )}
                          {partner.status === "approved" && (
                            <DropdownMenuItem onClick={() => handleStatusUpdate(partner.id, "suspended")}>
                              <XCircle className="w-4 h-4 mr-2 text-orange-600" /> Suspend
                            </DropdownMenuItem>
                          )}
                          {partner.status === "suspended" && (
                            <DropdownMenuItem onClick={() => handleStatusUpdate(partner.id, "approved")}>
                              <CheckCircle className="w-4 h-4 mr-2 text-green-600" /> Reactivate
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem 
                            onClick={() => handleDelete(partner.id)}
                            className="text-red-600"
                          >
                            <Trash2 className="w-4 h-4 mr-2" /> Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingPartner ? "Edit Partner" : "Register New Partner"}
            </DialogTitle>
            <DialogDescription>
              {editingPartner ? "Update partner information" : "Enter the details of the new retail partner"}
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Business Information */}
            <div className="space-y-4">
              <h4 className="font-medium text-slate-900">Business Information</h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label htmlFor="business_name">Business Name *</Label>
                  <Input
                    id="business_name"
                    value={formData.business_name}
                    onChange={(e) => setFormData({...formData, business_name: e.target.value})}
                    required
                    data-testid="input-business-name"
                  />
                </div>
                <div>
                  <Label htmlFor="business_type">Business Type *</Label>
                  <Select 
                    value={formData.business_type} 
                    onValueChange={(v) => setFormData({...formData, business_type: v})}
                  >
                    <SelectTrigger data-testid="select-business-type">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="clothing_retailer">Clothing Retailer</SelectItem>
                      <SelectItem value="department_store">Department Store</SelectItem>
                      <SelectItem value="discount_retailer">Discount Retailer</SelectItem>
                      <SelectItem value="thrift_store">Thrift Store</SelectItem>
                      <SelectItem value="nonprofit">Nonprofit Organization</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="website">Website</Label>
                  <Input
                    id="website"
                    value={formData.website}
                    onChange={(e) => setFormData({...formData, website: e.target.value})}
                    placeholder="www.example.com"
                    data-testid="input-website"
                  />
                </div>
              </div>
            </div>

            {/* Contact Information */}
            <div className="space-y-4">
              <h4 className="font-medium text-slate-900">Contact Information</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="contact_name">Contact Name *</Label>
                  <Input
                    id="contact_name"
                    value={formData.contact_name}
                    onChange={(e) => setFormData({...formData, contact_name: e.target.value})}
                    required
                    data-testid="input-contact-name"
                  />
                </div>
                <div>
                  <Label htmlFor="phone">Phone *</Label>
                  <Input
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    required
                    data-testid="input-phone"
                  />
                </div>
                <div className="col-span-2">
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    required
                    data-testid="input-email"
                  />
                </div>
              </div>
            </div>

            {/* Address */}
            <div className="space-y-4">
              <h4 className="font-medium text-slate-900">Location</h4>
              <div>
                <Label htmlFor="address">Street Address *</Label>
                <Input
                  id="address"
                  value={formData.address}
                  onChange={(e) => setFormData({...formData, address: e.target.value})}
                  required
                  data-testid="input-address"
                />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="city">City *</Label>
                  <Input
                    id="city"
                    value={formData.city}
                    onChange={(e) => setFormData({...formData, city: e.target.value})}
                    required
                    data-testid="input-city"
                  />
                </div>
                <div>
                  <Label htmlFor="state">State *</Label>
                  <Input
                    id="state"
                    value={formData.state}
                    onChange={(e) => setFormData({...formData, state: e.target.value})}
                    required
                    data-testid="input-state"
                  />
                </div>
                <div>
                  <Label htmlFor="zip_code">ZIP Code *</Label>
                  <Input
                    id="zip_code"
                    value={formData.zip_code}
                    onChange={(e) => setFormData({...formData, zip_code: e.target.value})}
                    required
                    data-testid="input-zip"
                  />
                </div>
              </div>
            </div>

            {/* Notes */}
            <div>
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                placeholder="Additional notes about the partner..."
                data-testid="input-notes"
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-amber-600 hover:bg-amber-700" data-testid="submit-btn">
                {editingPartner ? "Update" : "Register"} Partner
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Partners;
